<template>
  <div :id="'frdg_area_' + cntrSeq" class="select_opt_area">
    <h3 class="content_title mt10">{{ oogCgoTypCd === '03' ? 'In Gauge' : 'Out of Gauge' }}</h3>
    <booking-table-oog
      :key="oogCgoTypCd"
      ref="oog"
      :index="index"
      :parent-info="parentInfo"
      :cgo-typ-cd="oogCgoTypCd"
      :cntr-typ-cd="parentInfo.cntrTypCd"
      :kmd-vsl-check="kmdVslCheck"
      :empty-flag="emptyFlag"
      :lang="lang"
      :save-flag="saveFlag"
      :grs-wt-ton="grsWtTon"
      application-info-yn="N"
      @upload-attachments="openDgAttachments"
    />
    <h3 class="content_title mt10">{{ lang === 'KOR' ? '위험물' : 'DG' }}</h3>
    <booking-table-dg
      ref="dg"
      :index="index"
      :parent-info="parentInfo"
      :parent-dg-list="dgList"
      :cas-no-port-yn="casNoPortYn"
      :tel-ctr-no="telCtrNo"
      :disabled-yn="disabledYn"
      :unno-disabled-yn="unnoDisabledYn"
      :lang="lang"
      :save-flag="saveFlag"
      :bkg-sts-cd="bkgStsCd"
      :bkg-vsl-doc-yn="bkgVslDocYn"
      :status-class="statusClass"
      @vanningChk="$emit('vanningChk')"
    />
    <booking-dg-notice :lang="lang" />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import BookingTableDg from '@/components/trans/BookingTableDg.vue'
import BookingTableOog from '@/components/trans/BookingTableOog.vue'
import BookingDgNotice from '@/components/trans/BookingDgNotice.vue'

const OOG_FIELDS = [
  'orgLength', 'orgWidth', 'orgHeight', 'itemQty', 'underDckCd', 'bulkYn',
  'cmdtCd', 'cmdtDsc', 'oogCmdtNm', 'frgtWt', 'tareWtYn', 'itemCont'
]
const DG_FIELDS = ['containerDGList', 'reqConts', 'vaniDt']

/**
 * FR + DG 입력용 컴포넌트. 기존 컴포넌트처럼 #frm 안에서 사용하며,
 * parentInfo/index는 schedule.containerList의 동일한 행을 가리켜야 한다.
 * parentInfo.cgoTypCd는 DG('01'), oogCgoTypCd는 치수 검증용 OOG('02')/ING('03').
 *
 * 부모 저장 순서: await ref.validate() -> await ref.getContainerData().
 * Ref No./승인상태/첨부번호는 parentInfo 한 곳에서 관리하며 DG 영역에 표시한다.
 * OOG/DG 파일 버튼은 동일한 DG 첨부목록과 분류를 사용한다.
 * Booking 화면의 선택·저장 분기, 선택 유형의 저장/복원 및 Application 생성은
 * 부모/API에서 연결해야 하며 이 컴포넌트에서 신청이나 저장을 호출하지 않는다.
 */
export default {
  name: 'BookingTableFrDg',
  components: {
    BookingTableDg,
    BookingTableOog,
    BookingDgNotice
  },
  props: {
    parentInfo: {
      type: Object,
      required: true
    },
    parentDgList: {
      type: Array,
      default: null
    },
    index: {
      type: Number,
      required: true
    },
    oogCgoTypCd: {
      type: String,
      required: true,
      validator: value => value === '02' || value === '03'
    },
    kmdVslCheck: {
      type: String,
      default: 'N'
    },
    emptyFlag: {
      type: String,
      default: 'F'
    },
    grsWtTon: {
      type: String,
      default: ''
    },
    casNoPortYn: {
      type: String,
      default: 'N'
    },
    telCtrNo: {
      type: String,
      default: ''
    },
    disabledYn: {
      type: String,
      default: 'N'
    },
    unnoDisabledYn: {
      type: String,
      default: 'N'
    },
    lang: {
      type: String,
      default: ''
    },
    saveFlag: {
      type: String,
      default: ''
    },
    bkgStsCd: {
      type: String,
      default: ''
    },
    bkgVslDocYn: {
      type: String,
      default: ''
    },
    statusClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    cntrSeq () {
      return this.parentInfo.cntrSeq !== undefined && this.parentInfo.cntrSeq !== null
        ? Number(this.parentInfo.cntrSeq)
        : this.index + 1
    },
    dgList () {
      return this.parentDgList || this.parentInfo.containerDGList || []
    }
  },
  methods: {
    async openDgAttachments () {
      await this.waitForInputUpdate()
      if (this.$refs.dg) this.$refs.dg.uploadPopup()
    },
    isReady () {
      const { dg, oog } = this.$refs
      return this.parentInfo.cntrTypCd === 'FR' &&
        ['20', '40'].includes(String(this.parentInfo.cntrSzCd)) &&
        ['02', '03'].includes(this.oogCgoTypCd) &&
        Boolean(dg && oog && Number(dg.cntrSeq) === this.cntrSeq && Number(oog.cntrSeq) === this.cntrSeq)
    },
    async waitForInputUpdate () {
      // OOG는 parentInfo watcher에서 nextTick으로 initParam을 예약한다.
      await this.$nextTick()
      await this.$nextTick()
    },
    async validate () {
      await this.waitForInputUpdate()
      if (!this.isReady()) return false

      // 한 영역이 실패해도 두 영역의 필수값 안내를 모두 표시한다.
      const oogValid = this.$refs.oog.checkValidationOOGAll()
      const dgValid = await this.$refs.dg.checkValidationDGAll()
      return Boolean(oogValid && dgValid)
    },
    async getContainerData () {
      await this.waitForInputUpdate()
      if (!this.isReady()) {
        throw new Error('FRDG input is not ready. Use an FR 20/40 container.')
      }

      const { dg, oog } = this.$refs
      const container = cloneDeep(this.parentInfo)
      OOG_FIELDS.forEach(key => {
        container[key] = cloneDeep(oog.oogInfo[key])
      })
      DG_FIELDS.forEach(key => {
        container[key] = cloneDeep(dg.dgInfo[key])
      })

      // 자식의 reqRno/scgAppNo/승인정보로 공통 Application 값을 덮어쓰지 않는다.
      container.cgoTypCd = '01'
      container.cntrCgoTypCd = this.oogCgoTypCd
      container.vanningDtYn = dg.vanningDtYn
      container._simulation = oog.simulation
      container._deadSpace = oog.deadSpace
      container._overLength = oog.overLength
      container._overWidth = oog.overWidth
      container._overHeight = oog.overHeight
      return container
    }
  }
}
</script>
